import { useEffect, useState } from "react";
import { Check, Moon, Palette, Sun } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const KEY = "ciclo-theme";
const MODES = [["escuro", "Escuro", Moon], ["claro", "Claro", Sun]];
const ACCENTS = [["padrao", "Padrão", "#E2E8F0"], ["verde", "Verde", "#10B981"], ["azul", "Azul", "#3B82F6"], ["laranja", "Laranja", "#F97316"]];

const readPref = () => {
  try {
    return { mode: "escuro", accent: "padrao", ...JSON.parse(localStorage.getItem(KEY) || "{}") };
  } catch {
    return { mode: "escuro", accent: "padrao" };
  }
};

export const ThemePicker = ({ className = "" }) => {
  const [pref, setPref] = useState(readPref);

  useEffect(() => {
    const el = document.documentElement;
    el.classList.toggle("theme-light", pref.mode === "claro");
    if (pref.accent === "padrao") delete el.dataset.accent;
    else el.dataset.accent = pref.accent;
    localStorage.setItem(KEY, JSON.stringify(pref));
    return () => {
      el.classList.remove("theme-light");
      delete el.dataset.accent;
    };
  }, [pref]);

  const pick = (k, v) => setPref((p) => ({ ...p, [k]: v }));
  const chip = (active) => `flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors duration-150 ${active ? "border-slate-100 bg-slate-800 text-slate-50" : "border-slate-700 text-slate-300 hover:border-slate-500"}`;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button size="icon" variant="ghost" className={`h-9 w-9 shrink-0 rounded-full ${className}`} aria-label="Escolher tema" title="Tema do app" data-testid="theme-picker-button">
          <Palette className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 border-slate-700 bg-slate-900 p-3" data-testid="theme-picker">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">Tema</p>
        <div className="grid grid-cols-2 gap-2">
          {MODES.map(([k, label, Icon]) => (
            <button key={k} type="button" onClick={() => pick("mode", k)} className={chip(pref.mode === k)} data-testid={`theme-mode-${k}`}>
              <Icon className="h-4 w-4" /> {label}
            </button>
          ))}
        </div>
        <p className="mb-2 mt-4 text-xs font-semibold uppercase tracking-wider text-slate-400">Cor de destaque</p>
        <div className="grid grid-cols-2 gap-2">
          {ACCENTS.map(([k, label, hex]) => (
            <button key={k} type="button" onClick={() => pick("accent", k)} className={chip(pref.accent === k)} data-testid={`theme-accent-${k}`}>
              <span className="h-4 w-4 rounded-full border border-slate-600" style={{ background: hex }} /> {label}
              {pref.accent === k && <Check className="ml-auto h-3.5 w-3.5" />}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
